import MyButton from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import Input from "@/components/Input";
import { Col } from "@/styles/layout";
import { useState } from "react";
import { BiSave } from "react-icons/bi";
import { Controller, useForm } from "react-hook-form";
import AmountInput from "@/components/AmountInput";
import { toast } from "react-hot-toast";
import {
  CreateOfflineDonation,
  createOfflineDonation,
} from "@/services/donations.service";
import { BsPlusCircleFill } from "react-icons/bs";
import { useCampaigns } from "@/hooks/useCampaigns";
import { Body } from "@/styles/typography";
import TextArea from "@/components/TextArea";

interface Props {
  onSave: () => void;
}

const Modal = ({ onSave }: { onSave: () => void }) => {
  const { campaigns } = useCampaigns({
    initialPage: 0,
    limit: 100,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateOfflineDonation>({
    defaultValues: {},
  });

  const submit = async (input: CreateOfflineDonation) => {
    try {
      console.log(input);
      await createOfflineDonation(input);
      onSave();
      toast.success("Congratulations on your new offline donation!");
      onSave();
    } catch (e) {
      console.error(e);
      toast.error(
        "Something went wrong while creating your offline donation..."
      );
    }
  };

  return (
    <div className="space-y-5 p-6 min-w-[400px]">
      <div className="text-lg font-semibold text-center">
        Register an offline donation
      </div>
      <Col py="24px">
        <form onSubmit={handleSubmit(submit)}>
          <div className="space-y-1">
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="grossAmount"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <AmountInput
                    label="Amount"
                    containerStyle={{
                      borderRadius: "8px",
                      borderWidth: "1px",
                      borderColor: "primary.borderSubtle",
                      borderStyle: "solid",

                      height: 44,
                      marginRight: 16,
                      marginBottom: 0,
                    }}
                    min={5}
                    {...field}
                    value={(field.value ?? 0) / 100}
                    onChange={(e) => field.onChange(e * 100)}
                  />
                )}
              />
              <Controller
                name="campaignId"
                control={control}
                rules={{ required: false }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <label className="text-xs font-semibold">
                      <Body
                        color={error ? "danger.textLow" : "primary.textHigh"}
                        mb="4px"
                      >
                        CAMPAIGN
                      </Body>
                    </label>
                    <select
                      id="campaigns"
                      className="border border-pink-300 text-pink-900 text-sm rounded-md focus:ring-pink-500 focus:border-pink-500 block w-full p-2.5 "
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <option value="-1">Choose a campaign</option>
                      {campaigns.map((campaign) => (
                        <option
                          key={campaign.id}
                          value={campaign.id}
                          selected={field.value === campaign.id}
                        >
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              />

              <Controller
                name="firstName"
                control={control}
                defaultValue=""
                rules={{ required: false }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label="First name"
                    type="text"
                    containerStyle={{ marginBottom: "16px" }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                defaultValue=""
                rules={{ required: false }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label="Last name"
                    type="text"
                    containerStyle={{ marginBottom: "16px" }}
                    {...field}
                  />
                )}
              />
            </div>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  label="Email"
                  type="email"
                  containerStyle={{ marginBottom: "16px" }}
                  error={error ? "Add your email" : undefined}
                  {...field}
                />
              )}
            />
            <Controller
              name="company"
              control={control}
              defaultValue=""
              rules={{ required: false }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  label="Business"
                  type="text"
                  containerStyle={{ marginBottom: "16px" }}
                  {...field}
                />
              )}
            />

            <Controller
              name="comment"
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState: { error } }) => (
                <TextArea
                  label="Comment"
                  containerStyle={{
                    borderRadius: "4px",
                    borderWidth: "1px",
                    borderColor: "primary.borderSubtle",
                    borderStyle: "solid",
                    padding: "8px",
                    height: 60,
                    marginRight: 16,
                    marginBottom: 24,
                  }}
                  {...field}
                />
              )}
            />
          </div>
          <Col alignItems={"center"} justifyContent={"center"} mt="24px">
            <MyButton
              variant="primary"
              label="Add"
              isLoading={isSubmitting}
              containerStyle={{
                height: 44,
                borderRadius: 8,
                minWidth: 200,
                px: 32,
              }}
              labelStyle={{ fontSize: "16px" }}
              Icon={BiSave}
              type="submit"
            />
          </Col>
        </form>
      </Col>
    </div>
  );
};

const NewOfflineDonation = ({ onSave }: Props) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(e) => setOpen(e)}
      trigger={
        <div className="text-primary rounded-full bg-white cursor-pointer">
          <BsPlusCircleFill size={24} />
        </div>
      }
    >
      {open && (
        <Modal
          onSave={() => {
            setOpen(false);
            onSave();
          }}
        />
      )}
    </Dialog>
  );
};

export default NewOfflineDonation;
